import TimelineList from "examples/Timeline/TimelineList";
import TimelineItem from "examples/Timeline/TimelineItem";
import { Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  IconCirclePlus,
  IconChevronsRight,
  IconCircleCheck,
  IconTruckDelivery,
  IconCircleX,
  IconFileCheck,
  IconInfoCircle,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import TimelineItemSkeleton from "examples/Timeline/TimelineItem/TimelineItemSkeleton";

interface Proveedor {
  id: number;
  nombre_comercial: string;
}

interface Evento {
  id: number;
  nombre: string;
}

interface BitacoraItem {
  id: number;
  id_orden_compra: number;
  evento: Evento | null;
  created_at: string;
  orden_compra: OrdenCompraInfo | null;
}

interface OrdenCompraInfo {
  id: number;
  numero: string;
  id_proveedor: number;
  id_responsable: number;
  descripcion: string;
  computo: string;
  proveedor: Proveedor;
}

interface BitacoraTimelineProps {
  bitacora: BitacoraItem[];
  procesando?: boolean;
}

type ColorEvento =
  | "#22b6d1"
  | "#f6a40e"
  | "#e3818b"
  | "#476377"
  | "#85b9c2"
  | "#62c396"
  | "#91e3ab"
  | "#cc7533"
  | "#382870";

const eventoConfig: Record<number, { color: ColorEvento; icon: TablerIcon }> = {
  1: { color: "#22b6d1", icon: IconCirclePlus }, // Generada
  2: { color: "#f6a40e", icon: IconChevronsRight }, // Enviada
  4: { color: "#e3818b", icon: IconCircleCheck }, // Aceptada
  5: { color: "#476377", icon: IconTruckDelivery }, // Surtida
  6: { color: "#85b9c2", icon: IconCircleX }, // Rechazada
  7: { color: "#62c396", icon: IconFileCheck }, // XML Validado
};

const defaultConfig: { color: string; icon: TablerIcon } = {
  color: "#7b809a",
  icon: IconInfoCircle,
};

const BitacoraOrdenCompra = ({ bitacora, procesando }: BitacoraTimelineProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (procesando) {
    return (
      <>
        <Skeleton variant="text" width="45%" height={48} sx={{ mb: 1, mx: "auto" }} />
        <TimelineList title="">
          {[0, 1, 2, 3].map((i) => (
            <TimelineItemSkeleton
              key={i}
              position={isMobile ? "right" : i % 2 === 0 ? "left" : "right"}
            />
          ))}
        </TimelineList>
      </>
    );
  }

  if (!bitacora || bitacora.length === 0) return null;

  return (
    <>
      <Typography
        variant="h4"
        color="primary"
        gutterBottom
        sx={{ color: "#004c93", display: "block" }}
      >
        Bitácora de la orden de compra
      </Typography>
      <TimelineList title="">
        {bitacora.map((item, key) => {
          const config = eventoConfig[item.evento?.id ?? 0] ?? defaultConfig;
          const esEquipoComputo = item.orden_compra?.computo === "si";
          const esPrimero = key === 0;
          const esUltimo = key === bitacora.length - 1;
          const fecha = new Date(item.created_at).toLocaleString("es-MX", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          const descripcion =
            item.evento?.id === 1 && item.orden_compra ? (
              <>
                Se generó la orden de compra <strong>#{item.orden_compra.id}</strong> para el
                proveedor <strong>{item.orden_compra.proveedor.nombre_comercial}</strong> <br />
                {esEquipoComputo ? (
                  <i>
                    <b>Es equipo de cómputo</b>
                  </i>
                ) : null}
              </>
            ) : item.evento?.id === 2 ? (
              <>
                La orden se envío a validación con el Proveedor{" "}
                <strong>{item.orden_compra?.proveedor.nombre_comercial}</strong>
              </>
            ) : item.evento?.id === 4 ? (
              <>La orden fue aceptada por el Proveedor</>
            ) : item.evento?.id === 7 ? (
              <>La factura fue validada y subida por el Proveedor</>
            ) : item.evento?.id === 5 ? (
              <>La orden fue surtida</>
            ) : (
              <></>
            );

          return (
            <TimelineItem
              key={item.id}
              color={config.color}
              icon={config.icon}
              title={item.evento?.nombre ?? "Evento desconocido"}
              dateTime={`${fecha}`}
              //name={`${nombreUsuario}`}
              description={descripcion}
              firstItem={esPrimero}
              lastItem={esUltimo}
              position={key % 2 === 0 ? "left" : "right"}
            />
          );
        })}
      </TimelineList>
    </>
  );
};

export default BitacoraOrdenCompra;
